import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { Helper, TextField } from "../components/ui";
import { AuthSubmit } from "../components/AuthSubmit";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import { useI18n } from "../i18n";
import { useFormError } from "../useFormError";
import "./auth.css";

export default function VerificationPage() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pendingEmail, refresh, user } = useAuth();
  const { t } = useI18n();
  const [seconds, setSeconds] = useState(20);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const { error, setError, formRef } = useFormError();
  const [sent, setSent] = useState(false);
  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  const submitToken = useCallback(
    async (token: string) => {
      setBusy(true);
      setError(null);
      try {
        const hadSession = Boolean(user);
        await api.completeEmailVerification(token);
        await refresh();
        nav(hadSession ? "/onboarding" : "/");
      } catch (err) {
        setError(humanizeVerificationError(err, t));
      } finally {
        setBusy(false);
      }
    },
    [user, refresh, nav, t, setError],
  );

  useEffect(() => {
    if (autoSubmittedRef.current) return;
    const urlToken = searchParams.get("token");
    if (!urlToken) return;
    autoSubmittedRef.current = true;
    setCode(urlToken);
    setSearchParams({}, { replace: true });
    void submitToken(urlToken);
  }, [searchParams, setSearchParams, submitToken]);

  // An already-verified, logged-in user has nothing to do here — e.g. they
  // reloaded /verify after confirming, or opened it in a second tab. Send them
  // into the app instead of trapping them on the verification screen. Skip while
  // a code is in flight so the ?token= auto-submit and the post-registration →
  // /onboarding hand-off aren't preempted.
  useEffect(() => {
    if (user?.email_verified && !searchParams.has("token") && !busy) {
      nav("/overview", { replace: true });
    }
  }, [user, searchParams, busy, nav]);

  const maskedEmail = mask(pendingEmail ?? user?.email ?? null, t("auth.maskedFallback"));

  const resend = async () => {
    setBusy(true);
    setError(null);
    try {
      const resp = await api.startEmailVerification();
      setSent(true);
      // Backend tells us the exact cooldown — fall back to 20s if missing/zero.
      const cooldown = Math.max(0, Number(resp?.cooldown_remaining_seconds ?? 0));
      setSeconds(cooldown > 0 ? cooldown : 20);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.sendCodeFailed"));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const token = code.trim();
    if (!token) {
      setError(t("auth.verificationCodeRequired"));
      return;
    }
    if (token.length < 8) {
      setError(t("auth.verificationCodeShort"));
      return;
    }
    await submitToken(token);
  };

  return (
    <AuthLayout title={t("auth.verificationTitle")} showBack>
      <form ref={formRef} className="auth__form anim-stagger" onSubmit={onSubmit}>
        <div className="password-hint-group">
          <TextField
            placeholder={t("auth.verificationCodePlaceholder")}
            name="code"
            autoComplete="one-time-code"
            focused
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <Helper>
            {t("auth.codeSentTo", { email: maskedEmail })}{" "}
            {seconds > 0 ? (
              t("auth.canResendIn", { seconds })
            ) : (
              <button
                type="button"
                className="helper-link"
                onClick={resend}
                disabled={busy}
              >
                {t("auth.resendCode")}
              </button>
            )}
          </Helper>
        </div>
        {sent ? <p className="helper">{t("auth.freshCodeSent")}</p> : null}
        {error ? <p className="auth-error">{error}</p> : null}
        <AuthSubmit busy={busy}>{busy ? t("auth.checking") : t("auth.enter")}</AuthSubmit>
      </form>
    </AuthLayout>
  );
}

function humanizeVerificationError(
  err: unknown,
  t: (k: import("../i18n").TranslationKey, vars?: Record<string, string | number>) => string,
): string {
  const raw = err instanceof Error ? err.message : "";
  const lower = raw.toLowerCase();
  if (lower.includes("at least") && lower.includes("character")) {
    return t("auth.verificationCodeShort");
  }
  if (lower.includes("invalid") || lower.includes("already used") || lower.includes("expired")) {
    return t("auth.verificationInvalid");
  }
  if (lower.includes("rate") || lower.includes("too many")) {
    return t("auth.verificationTooMany");
  }
  return raw || t("auth.verificationFailed");
}

function mask(email: string | null, fallback: string): string {
  if (!email) return fallback;
  const [name, domain] = email.split("@");
  if (!domain) return email;
  const keep = Math.min(4, name.length);
  return `${name.slice(0, keep)}*****${domain}`;
}
